import { createContext, ReactNode, useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Post } from '../models/Post';
import { PostsService } from '../services/Posts.service';
import { hideSpinner, showSpinner } from '../store/slicers/globalSpinner.slicer';
import { showSnackbarAlert } from '../store/slicers/snackbarAlert.slicer';

interface IPostContext {
  posts: Post[];
  setPosts: (posts: Post[]) => void;
  loadMorePosts: () => Promise<void>;
  fetchPosts: () => Promise<void>;
  deletePost: (postId: number) => Promise<any>;
}

interface Props {
  children: ReactNode;
}

const PostContext = createContext<IPostContext>({} as IPostContext);

export const PostsContextProvider = ({ children }: Props) => {
  const dispatch = useDispatch();
  const [posts, setPosts] = useState<Post[]>([]);
  const [page, setPage] = useState(1);

  const PAGE_LIMIT = Number(process.env.REACT_APP_POSTS_PAGE_LIMIT);

  const loadMorePosts = async () => {
    dispatch(showSpinner());

    if (page == PAGE_LIMIT) {
      return;
    }

    let response = null;
    try {
      response = await PostsService.getPosts(page);
      if (response) {
        setPage(page + 1);
        setPosts([...posts, ...response.data.data.posts]);
      }
    } catch (error) {
      dispatch(
        showSnackbarAlert({
          title: 'Erro!',
          message: 'Ocorreu um erro ao carregar mais posts!',
          severity: 'error'
        })
      );
    } finally {
      dispatch(hideSpinner());
    }
  };

  const fetchPosts = async () => {
    dispatch(showSpinner());

    try {
      let response = await PostsService.getPosts(1);
      if (response && response?.data.data.posts.length > 0) {
        setPage(2);
        setPosts(response.data.data.posts);
      }
    } catch (error) {
      dispatch(
        showSnackbarAlert({
          title: 'Erro!',
          message: 'Ocorreu um erro ao carregar os posts!',
          severity: 'error'
        })
      );
    } finally {
      dispatch(hideSpinner());
    }
  };

  const deletePost = async (postId: number) => {
    if (!postId) return;
    dispatch(showSpinner());

    try {
      const response = await PostsService.deletePost(postId);
      if (response?.status == 200) {
        posts && setPosts(posts.filter((p) => p.id !== postId));
      }
      return response;
    } catch (error) {
      dispatch(
        showSnackbarAlert({
          title: 'Erro!',
          message: 'Ocorreu um erro ao deletar o post!',
          severity: 'error'
        })
      );
    } finally {
      dispatch(hideSpinner());
    }
  };

  return <PostContext.Provider value={{ posts, setPosts, loadMorePosts, fetchPosts, deletePost }}>{children}</PostContext.Provider>;
};

export const usePostsContext = () => useContext(PostContext);
